import { useEffect, useRef, useState } from 'react';
import { Smile } from 'lucide-react';
import { useT } from '../i18n';

// ANX-076 — лёгкий emoji-picker для composer'а оператора. Без внешних
// либ: курируемый набор нативных emoji, грид кнопок в popover'е. UX-паритет
// с визитёрской стороной (визитёр может слать emoji с системной клавиатуры).

const EMOJI: readonly string[] = [
  '😀', '😃', '😄', '😁', '😅', '😂', '🙂', '😉',
  '😊', '😍', '😘', '😎', '🤩', '🤔', '😐', '😴',
  '😢', '😭', '😡', '😱', '🤗', '🤝', '🙏', '👍',
  '👎', '👌', '👋', '✌️', '🤞', '💪', '🔥', '✨',
  '🎉', '🎊', '💯', '✅', '❌', '⚠️', '❤️', '💔',
  '⭐', '🌟', '💡', '📌', '📎', '📞', '✉️', '⏰',
  '🚀', '🛠️', '🐛', '💰', '🎁', '☕', '👀', '🙌',
];

export interface EmojiPickerProps {
  onSelect: (emoji: string) => void;
  disabled?: boolean;
}

export function EmojiPicker({ onSelect, disabled }: EmojiPickerProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Закрытие по клику снаружи и по Escape.
  useEffect(() => {
    if (!open) return;
    function onDocDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative flex">
      <button
        type="button"
        disabled={disabled}
        aria-label={t('conversation.emoji')}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
        }}
        className="flex items-center justify-center rounded-md border border-slate-200 px-3 text-slate-500 hover:bg-slate-50 hover:text-slate-700 disabled:opacity-50"
      >
        <Smile size={16} aria-hidden="true" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute bottom-full left-0 z-20 mb-1 grid w-64 grid-cols-8 gap-0.5 rounded-md border border-slate-200 bg-white p-2 shadow-pop"
        >
          {EMOJI.map((em) => (
            <button
              key={em}
              type="button"
              role="menuitem"
              // onMouseDown с preventDefault — не теряем фокус input'а раньше клика.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onSelect(em);
                setOpen(false);
              }}
              className="flex h-7 w-7 items-center justify-center rounded text-lg hover:bg-slate-100"
            >
              {em}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
